import React from 'react';
import { useUser } from '../providers/UserProvider';

const cn = (...classes: (string | undefined | null | false)[]): string => {
    return classes.filter(Boolean).join(' ');
};

type ImageSource = 'thumbnail' | 'original' | 'initials';

interface AvatarProps {
    className?: string;
    user?: { name: string; email: string };
    imageURL?: string;
    thumbnailURL?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
    className,
    user: userProp,
    imageURL,
    thumbnailURL
}) => {
    const { user: loggedInUser } = useUser();
    const user = userProp || loggedInUser;
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [computedFontSize, setComputedFontSize] = React.useState<string | undefined>(undefined);

    // Only use profile picture for the logged-in user's own avatar
    const isLoggedInUser = !userProp || userProp.email === loggedInUser?.email;
    const profilePicFallback = isLoggedInUser ? loggedInUser?.profilePictureUrl : undefined;
    const resolvedImageURL = imageURL || profilePicFallback || undefined;

    // State machine tracking which image source is currently active.
    // Start with thumbnail if provided, otherwise go straight to original.
    const initialSource: ImageSource = thumbnailURL
        ? 'thumbnail'
        : resolvedImageURL
          ? 'original'
          : 'initials';
    const [activeSource, setActiveSource] = React.useState<ImageSource>(initialSource);

    // Reset the state machine when the URLs change.
    React.useEffect(() => {
        setActiveSource(thumbnailURL ? 'thumbnail' : resolvedImageURL ? 'original' : 'initials');
    }, [thumbnailURL, resolvedImageURL]);

    const handleImageError = React.useCallback(() => {
        setActiveSource((prev) => {
            if (prev === 'thumbnail' && resolvedImageURL) return 'original';
            return 'initials';
        });
    }, [resolvedImageURL]);

    const activeSrc: string | undefined =
        activeSource === 'thumbnail'
            ? thumbnailURL
            : activeSource === 'original'
              ? resolvedImageURL
              : undefined;

    React.useEffect(() => {
        const element = containerRef.current;
        if (!element) return;
        const update = () => {
            const rect = element.getBoundingClientRect();
            const minSide = Math.max(0, Math.min(rect.width, rect.height));
            const px = Math.floor(minSide * 0.38);
            setComputedFontSize(px > 0 ? `${px}px` : undefined);
        };
        update();
        const win = globalThis as unknown as {
            ResizeObserver?: new (callback: ResizeObserverCallback) => ResizeObserver;
        };
        if (win.ResizeObserver) {
            const ro = new win.ResizeObserver(update);
            ro.observe(element);
            return () => ro.disconnect();
        }
    }, []);

    const getInitials = (name: string | undefined | null) => {
        const safeName = (name || '').trim();
        if (!safeName) return '?';
        // Strip honorifics so "Dr. Anas Louter" → initials from "Anas Louter".
        // Match a leading honorific token followed by an optional period and whitespace,
        // case-insensitive. Comma support handles "Louter, Anas" and "Dr Louter, Anas".
        const HONORIFIC_RE = /^(dr|doctor|mr|mrs|ms|miss|prof|professor)\.?\s+/i;
        const stripped = safeName
            .split(/[,\s]+/)
            .filter(Boolean)
            .join(' ')
            .replace(HONORIFIC_RE, '')
            .trim();
        const source = stripped || safeName;
        return source
            .split(/\s+/)
            .filter(Boolean)
            .map((word) => word.charAt(0))
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    if (!user) {
        return (
            <div
                className={cn(
                    'relative flex shrink-0 overflow-hidden rounded-full bg-gray-300',
                    'w-full h-full aspect-square',
                    className
                )}
            >
                <div className="flex h-full w-full items-center justify-center text-gray-500">
                    ?
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className={cn(
                'relative flex shrink-0 overflow-hidden rounded-full text-gray-600 font-bold items-center justify-center ring-2 ring-gray-200 bg-yellow-400',
                'w-full h-full aspect-square',
                className
            )}
            style={{
                fontSize: computedFontSize,
                letterSpacing: '0.05em',
                lineHeight: '1'
            }}
        >
            <div className="flex h-full w-full items-center justify-center min-w-0 overflow-hidden">
                {activeSrc ? (
                    <img
                        src={activeSrc}
                        alt={user.name}
                        className="h-full w-full object-cover"
                        onError={handleImageError}
                    />
                ) : (
                    getInitials(user.name)
                )}
            </div>
        </div>
    );
};
